import { CloseIcon } from "@chakra-ui/icons";
import { Box, Button, Grid, Text } from "@chakra-ui/react";
import { Checkbox } from "components/checkbox";
import { CustomModal } from "components/custom-modal";
import { InputField } from "components/input-field";
import { InputSearch } from "components/input-search";
import { Toast } from "components/toast";
import { useApplicationContext } from "contexts/ApplicationContext";
import { EmpresasEndpoints } from "components/empresas-components/utils";
import { createModalTrigger, useModalControls } from "hooks/use-modal-controls";
import { useState } from "react";
import { FaSave } from "react-icons/fa";
import { APIError } from "services/api-utils/api-creator";
import { ValidationError } from "utils/validate-fields";
import { isUserGerente } from "utils/is-gerente";
import { useFormFields } from "hooks/use-form-fields";
import { getEmpresaPayloadSchema, getInitialEmpresaDialog } from "./utils";
import {
  Comercial,
  EmpresaModalData,
  ModalEmpresaProps,
  Regional
} from "./types";

const eventName = "openModalEmpresa";

export function ModalEmpresa({
  onSaved,
  customerId,
  userType,
  empresa
}: ModalEmpresaProps) {
  const { user } = useApplicationContext();
  const userIsGerente = isUserGerente(user);
  const [comerciais, setComerciais] = useState<Comercial[]>([]);
  const [regionais, setRegionais] = useState<Regional[]>([]);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [
    empresaDialog,
    errors,
    onChangeEmpresaField,
    setEmpresaDialogFields,
    setErrors
  ] = useFormFields<EmpresaModalData>();

  const { isOpen, onClose, params } = useModalControls<ModalEmpresaProps>(
    eventName,
    {
      onOpen: (openedParams) => {
        const openedEmpresa = openedParams?.empresa ?? empresa;
        const openedCustomerId = openedParams?.customerId ?? customerId;

        console.log("OPENED EMPRESA =>", openedEmpresa);

        setEmpresaDialogFields(
          getInitialEmpresaDialog(openedEmpresa, openedCustomerId)
        );

        setErrors({});
        loadComerciais();
        loadRegionais();
      },
      onClose: () => {
        setEmpresaDialogFields(getInitialEmpresaDialog(undefined, customerId));
        setErrors({});
      }
    }
  );

  const currentUserType = params.userType ?? userType;

  const loadRegionais = async () => {
    try {
      const data = await EmpresasEndpoints.getRegionais();
      setRegionais(data);
    } catch (error) {
      Toast({
        title: "Erro ao carregar regionais",
        status: "error"
      });
    }
  };

  const loadComerciais = async () => {
    try {
      const data = await EmpresasEndpoints.getComerciais();
      setComerciais(data);
    } catch (error) {
      Toast({
        title: "Erro ao carregar comerciais",
        status: "error"
      });
    }
  };

  const onChangeEmpresaDialogCheckbox = (field: string, isChecked: boolean) => {
    onChangeEmpresaField(field as keyof EmpresaModalData, isChecked);
  };

  const saveOrUpdateEmpresa = async () => {
    setLoadingSave(true);
    try {
      const empresaPayloadSchema = getEmpresaPayloadSchema(userIsGerente);
      const validationResult = empresaPayloadSchema.validate(empresaDialog);
      const body = {
        ...validationResult.body
      } as EmpresaModalData;

      if (!!body.enabled3cplus && !body.token3cplus?.length) {
        throw new ValidationError({
          ...validationResult,
          errors: {
            ...validationResult.errors,
            token3cplus:
              "Token 3cplus é obrigatório quando a integração estiver ativa."
          },
          errorFields: [...validationResult.errorFields, "token3cplus"],
          isValid: false
        });
      }

      setErrors({});

      if (!empresaDialog.id) {
        await EmpresasEndpoints.createEmpresa(body);
      } else {
        await EmpresasEndpoints.updateEmpresa(empresaDialog.id, body);
      }
      await (params.onSaved ?? onSaved)?.();

      return true;
    } catch (error: any) {
      const errorMessage: Record<string, string> = {
        codigo_nova_exists: "Código Nova já cadastrado",
        not_allowed_create_colombo: "Código Nova não permitido",
        allowed_only_create_colombo: "Código Nova não permitido"
      };

      if (error instanceof ValidationError) {
        console.log("ERRORS =>", error.result.errors);
        setErrors(
          error.result.errors as Partial<Record<keyof EmpresaModalData, string>>
        );
        Toast({
          title: "Verifique os campos obrigatórios e tente novamente.",
          status: "warning"
        });
        return false;
      }

      const message =
        error instanceof APIError
          ? (error.data as { message?: string })?.message
          : error?.response?.data?.message;

      if (message === "codigo_nova_exists") {
        setErrors((prev) => ({
          ...prev,
          codigoNova: "Código Nova já cadastrado"
        }));
      }

      Toast({
        title: errorMessage[message] ?? "Erro ao salvar empresa!",
        status: "error"
      });
      return false;
    } finally {
      setLoadingSave(false);
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      modalTitle="Empresa"
      size="4xl"
      modalFooter={
        <>
          <Button
            leftIcon={<FaSave />}
            onClick={async () => {
              const wasSaved = await saveOrUpdateEmpresa();
              if (wasSaved) onClose();
            }}
            isLoading={loadingSave}
            loadingText="Salvando"
          >
            Salvar
          </Button>
          <Button
            variant="outline"
            leftIcon={<CloseIcon w="11px" h="11px" />}
            onClick={onClose}
          >
            Cancelar
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Grid templateColumns="85px auto" gap="16px" mb="16px">
        <Checkbox
          isChecked={!!empresaDialog.enabled}
          onChange={(isChecked) =>
            onChangeEmpresaDialogCheckbox("enabled", isChecked)
          }
        >
          Ativo
        </Checkbox>
        <Box>
          <InputField
            title="Nome da Empresa"
            value={empresaDialog.name || ""}
            isInvalid={!!errors.name}
            errorMessage={errors.name}
            inputProps={{ placeholder: "Digite o nome da empresa" }}
            onChange={(e) => onChangeEmpresaField("name", e.target.value)}
          />
        </Box>
      </Grid>
      <Grid templateColumns="1fr 1fr" gap="16px" mb="16px">
        <Box>
          <InputField
            title="CNPJ"
            value={empresaDialog.cnpj || ""}
            onChange={(e) => onChangeEmpresaField("cnpj", e.target.value)}
          />
        </Box>
        <Grid
          templateColumns={userIsGerente ? "1fr 1fr" : "1fr"}
          gap="16px"
          mb="16px"
        >
          <Box>
            <InputField
              title="Código Nova"
              value={empresaDialog.codigoNova || ""}
              inputProps={{
                autoComplete: "new-password",
                placeholder: "Digite o Código Nova"
              }}
              onChange={(e) => {
                onChangeEmpresaField("codigoNova", e.target.value);
              }}
              isInvalid={!!errors.codigoNova}
              errorMessage={errors.codigoNova}
            />
          </Box>
          {userIsGerente && (
            <Box>
              <InputField
                title="Código Externo"
                value={empresaDialog.codigoExterno || ""}
                isInvalid={!!errors.codigoExterno}
                errorMessage={errors.codigoExterno}
                onChange={(e) => {
                  onChangeEmpresaField("codigoExterno", e.target.value);
                }}
              />
            </Box>
          )}
        </Grid>
      </Grid>
      <Grid templateColumns="1fr 1fr" gap="16px" mb="16px">
        <Box>
          <Text mb="8px">Regional Responsável</Text>
          <InputSearch
            value={
              regionais.find((r) => r.id === empresaDialog.regionalCustomer?.id)
                ?.name || ""
            }
            onChange={(value) =>
              onChangeEmpresaField("regionalCustomer", {
                id: value,
                name: regionais.find((r) => r.id === value)?.name || ""
              })
            }
            options={regionais.map(({ name, id }) => ({ name, value: id }))}
            inputProps={{
              placeholder: "Selecione",
              isDisabled: regionais && regionais.length === 1
            }}
            checkIsInclude={(option) =>
              option.value === empresaDialog.regionalCustomer?.id
            }
            openOnFocus
            partialSearch
            closeOnSelect
          />
        </Box>
        <Box>
          <Text mb="8px">Comercial Responsável</Text>
          <InputSearch
            value={
              comerciais.find((c) => c.id === empresaDialog.comercialUser?.id)
                ?.name || ""
            }
            onChange={(value) =>
              onChangeEmpresaField("comercialUser", {
                id: value,
                name: comerciais.find((c) => c.id === value)?.name || ""
              })
            }
            options={comerciais.map(({ name, id }) => ({
              name,
              value: id
            }))}
            inputProps={{ placeholder: "Selecione" }}
            checkIsInclude={(option) =>
              option.value === empresaDialog.comercialUser?.id
            }
            openOnFocus
            partialSearch
            closeOnSelect
          />
        </Box>
      </Grid>
      <Grid templateColumns="1fr 1fr" gap="16px" mb="16px">
        <Box>
          <InputField
            title="E-mail"
            value={empresaDialog.email || ""}
            onChange={(e) => onChangeEmpresaField("email", e.target.value)}
          />
        </Box>
        <Box>
          <InputField
            title="Telefone"
            value={empresaDialog.telefone || ""}
            onChange={(e) => onChangeEmpresaField("telefone", e.target.value)}
          />
        </Box>
      </Grid>
      {currentUserType === "SUPER" ? (
        <Grid templateColumns="85px auto" gap="16px" mb="16px">
          <Box>
            <Text mb="8px">&nbsp;</Text>
            <Checkbox
              isChecked={!!empresaDialog.enabled3cplus}
              onChange={(isChecked) =>
                onChangeEmpresaDialogCheckbox("enabled3cplus", isChecked)
              }
            >
              3cplus
            </Checkbox>
          </Box>
          <Box>
            <InputField
              title="Token 3cplus"
              value={empresaDialog.token3cplus || ""}
              isInvalid={!!errors.token3cplus}
              errorMessage={errors.token3cplus}
              onChange={(e) =>
                onChangeEmpresaField("token3cplus", e.target.value)
              }
            />
          </Box>
        </Grid>
      ) : null}
    </CustomModal>
  );
}

export const openModalEmpresa =
  createModalTrigger<ModalEmpresaProps>(eventName);
